import { useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
} from '@product-framework/index';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function RuleCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'ruleFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/rules`;

  const preProcessRequestData = data => {
    const { conditions, outcome, priority, isActive, ...other } = data;
    return {
      isActive: isActive === 'true',
      priority: priority ? parseInt(priority, 10) : 0,
      conditions: conditions ? JSON.parse(conditions) : {},
      outcome: outcome ? JSON.parse(outcome) : {},
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'RULE_CREATE_SUCCESS',
          defaultMessage: 'Rule was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/rules/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'RULE_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the rule',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'RULES_BREADCRUMB',
        defaultMessage: 'Rules',
      }),
      icon: IconLibrary.Rules,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/rules');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_RULE_BREADCRUMB',
        defaultMessage: 'Create Rule',
      }),
      icon: IconLibrary.Rule,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_RULES_STACKABLE_BUTTON',
        defaultMessage: 'List Rules',
      }),
      Icon: IconLibrary.Rules,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/rules');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'RULE_CREATE_HEADING',
          defaultMessage: 'Create Rule',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <Button
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'RULE_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Rule',
                })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'RULE_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </Button>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(RuleCreate);
